import {
  Dispatch,
  SetStateAction,
  createContext,
  useCallback,
  useEffect,
  useState,
} from 'react'
import SwiperCore, { Keyboard, Mousewheel } from 'swiper'
import { Swiper as ReactSwiper, SwiperSlide } from 'swiper/react'
import { useNavigate } from 'react-router-dom'
import PermIdentityOutlinedIcon from '@mui/icons-material/PermIdentityOutlined'

import { useFeedStore } from '../../store/feed-store'
import styles from './Feed.module.css'
import SoundControl from '../../components/SoundControlButton/SoundControlButton'
import { Feed, FeedType, FeedbackFeed } from '../../types/Feed'
import { authFetch } from '../../common/utils/cognito'
import FeedItem from '../../components/Feed/FeedItem'
import { useTherapistStore } from '../../store/therapist-store'
import { useUserStore } from '../../store/user-store'

// Trigger a therapist post in the feed after every n fetches
const THERAPIST_FEED_FREQUENCY = 8
// Trigger a feedback post in the feed after n items
const FEEDBACK_FEED_TRIGGER = 100

SwiperCore.use([Keyboard, Mousewheel])

interface ISwiperContext {
  setShowSoundControl?: Dispatch<SetStateAction<boolean>>
  swiper?: SwiperCore
}

export const SwiperContext = createContext<ISwiperContext>({})

const FeedPage = () => {
  const navigate = useNavigate()
  const [swiper, setSwiper] = useState<SwiperCore>()
  const { currentUser } = useUserStore()

  const { therapists, fetch: fetchTherapists } = useTherapistStore()
  const {
    currentFeedActiveIndex,
    feed,
    fetchCount,
    setCurrentFeedActiveIndex,
    setCurrentStep,
    setFeed,
    stepperVisible,
  } = useFeedStore()

  const [isLoading, setIsLoading] = useState(false)
  const [currentTherapistIndex, setCurrentTherapistIndex] = useState(0)
  const [showSoundControl, setShowSoundControl] = useState(false)

  const feedUrl = `${process.env.REACT_APP_API_URL}/feed`
  const fetchFeed = async () => {
    const response = await authFetch(feedUrl)
    const newFeedItems = (await response.json()) as Feed[]

    const updatedFeed = newFeedItems.reduce<Feed[]>((accumulator, current) => {
      if (
        accumulator.findIndex(
          ({ id, type }) => current.id === id && current.type === type,
        ) === -1
      ) {
        accumulator.push(current)
      }

      return accumulator
    }, feed)

    if (fetchCount % THERAPIST_FEED_FREQUENCY === 0) {
      const therapist = therapists.at(currentTherapistIndex)

      if (therapist !== undefined) {
        updatedFeed.push(therapist)
      }

      if (currentTherapistIndex + 1 > therapists.length) {
        setCurrentTherapistIndex(0)
      } else {
        setCurrentTherapistIndex(currentTherapistIndex + 1)
      }
    }

    if (
      currentUser?.appRating === null &&
      updatedFeed.length >= FEEDBACK_FEED_TRIGGER
    ) {
      updatedFeed.push({
        id: 0,
        type: FeedType.FEEDBACK,
        content: {},
      } as FeedbackFeed)
    }

    setFeed(updatedFeed)
  }

  useEffect(() => {
    fetchTherapists()
  }, [fetchTherapists])

  const updateContentViewCount = async (mediaPostId: number) => {
    await authFetch(`${process.env.REACT_APP_API_URL}/user-media-posts`, {
      method: 'POST',
      body: JSON.stringify({
        mediaPostId,
      }),
    })
  }

  const setFeedControls = useCallback(
    (activeIndex: number = 0) => {
      if (feed.length === 0) {
        return
      }

      const currentFeedItem = feed[activeIndex]
      navigate(`/feed?id=${currentFeedItem.id}&type=${currentFeedItem.type}`)

      if (currentFeedItem.type === FeedType.CONTENT) {
        updateContentViewCount(currentFeedItem.content.id)
      }

      if (
        currentFeedItem.type === FeedType.EXERCISE &&
        currentFeedItem.content.steps !== undefined &&
        currentFeedItem.content.steps.length > 1
      ) {
        setCurrentStep(1)
      } else if (
        currentFeedItem.type === FeedType.RESULT &&
        currentFeedItem.content.results.length > 1
      ) {
        setCurrentStep(1)
      }
    },
    [feed, navigate, setCurrentStep],
  )

  useEffect(() => {
    setFeedControls()
  }, [feed, setFeedControls])

  const onSlideChange = (swiper: SwiperCore) => {
    setFeedControls(swiper.activeIndex)
    setCurrentFeedActiveIndex(swiper.activeIndex)
  }

  const onProfileClick = () => {
    navigate('/profile')
  }

  const onReachEnd = async () => {
    if (isLoading) {
      return
    }

    setIsLoading(true)
    await fetchFeed()
    setIsLoading(false)
  }

  return (
    <>
      <div
        className={`${styles.icons} ${
          stepperVisible ? styles.iconsWithStepper : ''
        }`}
      >
        <span onClick={onProfileClick}>
          <PermIdentityOutlinedIcon />
        </span>
        <SoundControl visible={showSoundControl} />
      </div>
      <ReactSwiper
        onSwiper={setSwiper}
        onReachEnd={onReachEnd}
        initialSlide={currentFeedActiveIndex}
        style={{ height: '100dvh', position: 'relative' }}
        direction="vertical"
        mousewheel
        keyboard
        onSlideChange={onSlideChange}
        watchSlidesProgress
      >
        {feed.map((feedItem, index) => (
          <SwiperSlide key={index}>
            <SwiperContext.Provider value={{ swiper, setShowSoundControl }}>
              <FeedItem post={feedItem} />
            </SwiperContext.Provider>
          </SwiperSlide>
        ))}
      </ReactSwiper>
    </>
  )
}

export default FeedPage
